import type { FunctionComponent, KeyboardEvent, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { findMainRegion, focusRegion } from '~/ui/core/focus/focus-regions';

import { scrollTileViewport } from './tile-scroll-navigation';
import type { ParsedUrlTile, UrlTileRenderContext } from './types';

type EscapeCloseHandler = () => void;

type UrlTileFocusScopeContextType = {
    readonly registerEscapeClose: (handler: EscapeCloseHandler) => () => void;
};

const UrlTileFocusScopeContext = createContext<UrlTileFocusScopeContextType | null>(null);

export const useUrlTileEscapeClose = (handler: EscapeCloseHandler | undefined): void => {
    const context = useContext(UrlTileFocusScopeContext);

    useEffect(() => {
        if (context === null || handler === undefined) {
            return;
        }

        return context.registerEscapeClose(handler);
    }, [context, handler]);
};

export type UrlTileFocusRegionProps = PropsWithChildren<{
    readonly ariaLabel: string;
    // Identity of what the region shows. A change re-arms focus without remounting.
    readonly focusKey: string;
    // Escape fallback for regions whose content registers no handler of its own.
    readonly onEscapeClose?: EscapeCloseHandler;
    readonly regionKey: string;
}>;

// The focus and Escape behaviour every tile-sized surface gets, whether it is backed by a
// parsed url tile or synthesized next to one (the comments group tile).
export const UrlTileFocusRegion: FunctionComponent<UrlTileFocusRegionProps> = ({
    ariaLabel,
    children,
    focusKey,
    onEscapeClose,
    regionKey,
}) => {
    const rootRef = useRef<HTMLElement>(null);
    const scrollViewportRef = useRef<HTMLElement | null>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const escapeCloseHandlerRef = useRef<EscapeCloseHandler | null>(null);
    const escapeFallbackRef = useRef(onEscapeClose);
    escapeFallbackRef.current = onEscapeClose;

    // Re-arms on tile identity change (opening item B while item A's tile is
    // open, or paging): focus re-arrives at the tile root and the close-restore
    // target becomes the new opener. Runs as an effect instead of a React key
    // remount so in-tile state (comments rail, scroll, form drafts) survives
    // payload swaps.
    // biome-ignore lint/correctness/useExhaustiveDependencies: focusKey is the re-arm trigger, not read in the body
    useEffect(() => {
        const opener = document.activeElement;
        const root = rootRef.current;

        if (root !== null) {
            if (opener instanceof HTMLElement && opener !== document.body && !root.contains(opener)) {
                openerRef.current = opener;
            }

            root.focus({ preventScroll: true });
        }
    }, [focusKey]);

    useEffect(() => {
        const root = rootRef.current;

        return () => {
            const previousOpener = openerRef.current;
            const activeElement = document.activeElement;
            const focusWasInside =
                activeElement === null || activeElement === document.body || root?.contains(activeElement) === true;

            if (!focusWasInside) {
                return;
            }

            if (previousOpener?.isConnected === true) {
                previousOpener.focus({ preventScroll: true });

                return;
            }

            const mainRegion = findMainRegion();

            if (mainRegion !== null) {
                focusRegion(mainRegion, { preventScroll: true });
            }
        };
    }, []);

    const registerEscapeClose = useCallback((handler: EscapeCloseHandler): (() => void) => {
        escapeCloseHandlerRef.current = handler;

        return () => {
            if (escapeCloseHandlerRef.current === handler) {
                escapeCloseHandlerRef.current = null;
            }
        };
    }, []);

    const handleEscape = useCallback((event: KeyboardEvent<HTMLElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        if (event.target === rootRef.current) {
            const escapeClose = escapeCloseHandlerRef.current ?? escapeFallbackRef.current;

            escapeClose?.();

            return;
        }

        rootRef.current?.focus();
    }, []);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLElement>): void => {
            if (event.defaultPrevented) {
                return;
            }

            if (event.key === 'Escape') {
                handleEscape(event);

                return;
            }

            if (event.target === rootRef.current && scrollTileViewport(rootRef.current, event, scrollViewportRef)) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [handleEscape],
    );

    const contextValue = useMemo(() => ({ registerEscapeClose }), [registerEscapeClose]);

    return (
        <UrlTileFocusScopeContext.Provider value={contextValue}>
            <section
                aria-label={ariaLabel}
                className="group/tile relative flex h-full max-h-full min-h-0 w-full flex-col rounded-xxl outline-none"
                data-focus-entry=""
                data-focus-region={regionKey}
                data-focus-region-kind="tile"
                ref={rootRef}
                tabIndex={-1}
                onKeyDown={handleKeyDown}
            >
                {children}
                <div
                    aria-hidden={true}
                    className="pointer-events-none invisible absolute inset-0 z-10 rounded-xxxl group-focus-visible/tile:visible"
                    data-testid="active-tile-ring"
                    style={{ boxShadow: 'inset 0 0 0 2px var(--color-gray-900)' }}
                />
            </section>
        </UrlTileFocusScopeContext.Provider>
    );
};

export const UrlTileFocusScope: FunctionComponent<
    PropsWithChildren<{
        readonly context: UrlTileRenderContext;
        readonly tile: ParsedUrlTile;
    }>
> = ({ children, context, tile }) => (
    <UrlTileFocusRegion
        ariaLabel={tile.definition.type}
        focusKey={tile.tileKey}
        regionKey={tile.slotKey}
        onEscapeClose={() => context.closeTile(tile.slotKey)}
    >
        {children}
    </UrlTileFocusRegion>
);
